import React, { useEffect, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import {
  ActionIcon,
  Button,
  FileButton,
  Group,
  Modal,
  Progress,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import {
  Cross2Icon,
  EyeOpenIcon,
  PlusIcon,
  UploadIcon,
} from '@radix-ui/react-icons'

import {
  canPreviewLocalFile,
  LocalFilePreviewModal,
} from './ExhibitFilePreview'
import {
  DESIGNATION_OPTIONS,
  EXHIBIT_FILE_ACCEPT,
  INCLUSION_OPTIONS,
  isAcceptedExhibitFile,
  isDesignated,
  MAX_EXHIBITS_PER_SAVE,
  notifyUnsupported,
} from './exhibits'

import { ExhibitInclusion, ExhibitRecordDesignation } from '@/apiClient'
import {
  ExhibitSubmissionEvent,
  NewExhibit,
  useAddExhibitsMutation,
} from '@/queries/courtReporterExhibits'
import { getApiErrorMessage } from '@/utils/apiError'

type Row = {
  key: number
  file?: File
  name: string
  description: string
  designation: ExhibitRecordDesignation | null
  inclusion: ExhibitInclusion | null
  notes: string
  progress?: number
}

const HINT =
  'Add an exhibit, upload files, or drag files anywhere onto this window.'

export const AddExhibitsModal: React.FC<{
  event: ExhibitSubmissionEvent
  opened: boolean
  existingCount: number
  // Files dropped on the Exhibits section, which open the modal with a row each.
  initialFiles?: File[]
  onClose: () => void
}> = ({ event, opened, existingCount, initialFiles, onClose }) => {
  const [rows, setRows] = useState<Row[]>([])
  const [validated, setValidated] = useState(false)
  const [previewing, setPreviewing] = useState<File | null>(null)
  const nextKey = useRef(0)
  // Only counts up, so removing a row never hands its number to the next one.
  const nextNumber = useRef(1)
  const { mutate: addExhibits, isLoading: saving } =
    useAddExhibitsMutation(event)

  const newRow = (file?: File): Row => ({
    key: nextKey.current++,
    file,
    name: `Exhibit ${existingCount + nextNumber.current++}`,
    description: file?.name ?? '',
    designation: null,
    inclusion: null,
    notes: '',
  })

  const addFiles = (files: File[]) => {
    const accepted = files.filter(isAcceptedExhibitFile)
    files
      .filter(file => !isAcceptedExhibitFile(file))
      .forEach(notifyUnsupported)
    const added = accepted.map(file => newRow(file))
    setRows(current => [...current, ...added])
  }

  useEffect(() => {
    if (opened && initialFiles && initialFiles.length > 0) {
      addFiles(initialFiles)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, initialFiles])

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    disabled: saving,
    onDrop: addFiles,
  })

  const updateRow = (key: number, changes: Partial<Row>) =>
    setRows(current =>
      current.map(row => (row.key === key ? { ...row, ...changes } : row)),
    )
  const updateAll = (changes: Partial<Row>) =>
    setRows(current => current.map(row => ({ ...row, ...changes })))

  const reset = () => {
    setRows([])
    setValidated(false)
    nextNumber.current = 1
  }

  const close = () => {
    if (saving) {
      return
    }
    reset()
    onClose()
  }

  const hasMissingFields = rows.some(
    row => !row.name.trim() || !isDesignated(row.designation),
  )
  const hasTooMany = rows.length > MAX_EXHIBITS_PER_SAVE

  const save = () => {
    setValidated(true)
    // Checked before any file uploads, so a refused save wastes none of them.
    if (hasMissingFields || hasTooMany) {
      return
    }

    const exhibits = new Map<NewExhibit, number>(
      rows.map(row => [
        {
          file: row.file,
          fields: {
            name: row.name.trim(),
            description: row.description,
            designation: row.designation ?? ExhibitRecordDesignation.unknown,
            inclusion: row.inclusion ?? ExhibitInclusion.unknown,
            notes: row.notes,
          },
        },
        row.key,
      ]),
    )
    addExhibits(
      {
        exhibits: [...exhibits.keys()],
        onProgress: (exhibit, progress) =>
          updateRow(exhibits.get(exhibit) as number, { progress }),
      },
      {
        onSuccess: () => {
          showNotification({ color: 'green', message: 'Exhibits added.' })
          reset()
          onClose()
        },
        onError: error => {
          updateAll({ progress: undefined })
          showNotification({
            color: 'red',
            message: getApiErrorMessage(error, 'Could not add the exhibits'),
          })
        },
      },
    )
  }

  return (
    <>
      <Modal
        opened={opened && previewing === null}
        onClose={close}
        title="Add exhibits"
        size="90%"
        centered
        closeOnClickOutside={!saving && rows.length === 0}
        closeOnEscape={!saving && rows.length === 0}
        withCloseButton={!saving}
      >
        <div {...getRootProps()} className="relative">
          <input {...getInputProps()} accept={EXHIBIT_FILE_ACCEPT} />
          {isDragActive && (
            <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded border-2 border-dashed border-indigo-500 bg-white/95">
              <UploadIcon width={32} height={32} />
              <Text weight={600}>Drop exhibit files</Text>
              <Text size="sm" color="dimmed">
                Each file adds a new exhibit. Files upload when you save.
              </Text>
            </div>
          )}

          <Stack>
            <Text size="sm" color="dimmed">
              {HINT}
            </Text>

            {rows.length > 0 && (
              <ScrollArea>
                <Table
                  verticalSpacing="xs"
                  className="min-w-[1160px] table-fixed"
                >
                  <thead>
                    <tr>
                      <th className="w-56">File</th>
                      <th className="w-48">Exhibit number *</th>
                      <th>Description</th>
                      <th className="w-44">
                        <Stack spacing={4}>
                          Designation *
                          <Select
                            aria-label="Designation for all exhibits"
                            placeholder="Set for all"
                            size="xs"
                            data={DESIGNATION_OPTIONS}
                            value={null}
                            onChange={designation =>
                              updateAll({
                                designation:
                                  designation as ExhibitRecordDesignation,
                              })
                            }
                            withinPortal
                          />
                        </Stack>
                      </th>
                      <th className="w-44">
                        <Stack spacing={4}>
                          Inclusion
                          <Select
                            aria-label="Inclusion for all exhibits"
                            placeholder="Set for all"
                            size="xs"
                            data={INCLUSION_OPTIONS}
                            value={null}
                            onChange={inclusion =>
                              updateAll({
                                inclusion: inclusion as ExhibitInclusion,
                              })
                            }
                            withinPortal
                          />
                        </Stack>
                      </th>
                      <th>Notes</th>
                      <th className="w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(row => (
                      <tr key={row.key}>
                        <td className="align-top">
                          <RowFile
                            onPreview={setPreviewing}
                            row={row}
                            disabled={saving}
                            onChange={file => {
                              if (file && !isAcceptedExhibitFile(file)) {
                                notifyUnsupported(file)
                                return
                              }
                              updateRow(row.key, {
                                file: file ?? undefined,
                                description:
                                  row.description || (file?.name ?? ''),
                              })
                            }}
                          />
                        </td>
                        <td className="align-top">
                          <TextInput
                            aria-label="Exhibit number"
                            size="xs"
                            value={row.name}
                            disabled={saving}
                            error={validated && !row.name.trim()}
                            onChange={e =>
                              updateRow(row.key, {
                                name: e.currentTarget.value,
                              })
                            }
                          />
                        </td>
                        <td className="align-top">
                          <Textarea
                            aria-label="Description"
                            size="xs"
                            autosize
                            minRows={1}
                            maxRows={6}
                            value={row.description}
                            disabled={saving}
                            onChange={e =>
                              updateRow(row.key, {
                                description: e.currentTarget.value,
                              })
                            }
                          />
                        </td>
                        <td className="align-top">
                          <Select
                            aria-label="Designation"
                            placeholder="Select"
                            size="xs"
                            data={DESIGNATION_OPTIONS}
                            value={row.designation}
                            disabled={saving}
                            error={validated && !isDesignated(row.designation)}
                            onChange={designation =>
                              updateRow(row.key, {
                                designation:
                                  designation as ExhibitRecordDesignation | null,
                              })
                            }
                            withinPortal
                          />
                        </td>
                        <td className="align-top">
                          <Select
                            aria-label="Inclusion"
                            placeholder="Select"
                            size="xs"
                            data={INCLUSION_OPTIONS}
                            value={row.inclusion}
                            disabled={saving}
                            clearable
                            onChange={inclusion =>
                              updateRow(row.key, {
                                inclusion: inclusion as ExhibitInclusion | null,
                              })
                            }
                            withinPortal
                          />
                        </td>
                        <td className="align-top">
                          <Textarea
                            aria-label="Notes"
                            placeholder="Anything Filevine should know"
                            size="xs"
                            autosize
                            minRows={1}
                            maxRows={6}
                            value={row.notes}
                            disabled={saving}
                            onChange={e =>
                              updateRow(row.key, {
                                notes: e.currentTarget.value,
                              })
                            }
                          />
                        </td>
                        <td className="align-top">
                          <ActionIcon
                            aria-label="Remove this exhibit"
                            disabled={saving}
                            onClick={() =>
                              setRows(current =>
                                current.filter(r => r.key !== row.key),
                              )
                            }
                          >
                            <Cross2Icon />
                          </ActionIcon>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ScrollArea>
            )}

            {validated && hasMissingFields && (
              <Text size="sm" color="red">
                Every exhibit needs a number and a designation.
              </Text>
            )}
            {validated && hasTooMany && (
              <Text size="sm" color="red">
                Add at most {MAX_EXHIBITS_PER_SAVE} exhibits at a time.
              </Text>
            )}

            <Group position="apart">
              <Group spacing="xs">
                <Button
                  variant="default"
                  leftIcon={<PlusIcon />}
                  disabled={saving}
                  onClick={() => {
                    const row = newRow()
                    setRows(current => [...current, row])
                  }}
                >
                  {rows.length === 0 ? 'Add exhibit' : 'Add another exhibit'}
                </Button>
                <Button
                  variant="default"
                  leftIcon={<UploadIcon />}
                  disabled={saving}
                  onClick={open}
                >
                  Upload files
                </Button>
              </Group>
              <Group spacing="xs">
                <Button
                  variant="light"
                  color="gray"
                  disabled={saving}
                  onClick={close}
                >
                  Cancel
                </Button>
                <Button
                  disabled={rows.length === 0}
                  loading={saving}
                  onClick={save}
                >
                  Save
                </Button>
              </Group>
            </Group>
          </Stack>
        </div>
      </Modal>
      <LocalFilePreviewModal
        file={previewing}
        onClose={() => setPreviewing(null)}
      />
    </>
  )
}

const RowFile: React.FC<{
  row: Row
  disabled: boolean
  onChange: (file: File | null) => void
  onPreview: (file: File) => void
}> = ({ row, disabled, onChange, onPreview }) => {
  const resetFileInput = useRef<() => void>(null)

  if (!row.file) {
    return (
      <FileButton
        accept={EXHIBIT_FILE_ACCEPT}
        resetRef={resetFileInput}
        onChange={file => {
          resetFileInput.current?.()
          onChange(file)
        }}
      >
        {props => (
          <Button
            {...props}
            size="xs"
            variant="subtle"
            compact
            leftIcon={<UploadIcon />}
            disabled={disabled}
          >
            Choose file
          </Button>
        )}
      </FileButton>
    )
  }

  return (
    <Stack spacing={4}>
      <Group spacing={4} noWrap>
        <Text size="sm" className="min-w-0 truncate" title={row.file.name}>
          {row.file.name}
        </Text>
        {canPreviewLocalFile(row.file) && (
          <ActionIcon
            size="xs"
            aria-label={`Preview ${row.file.name}`}
            onClick={() => onPreview(row.file as File)}
          >
            <EyeOpenIcon />
          </ActionIcon>
        )}
        <ActionIcon
          size="xs"
          aria-label="Remove the file"
          disabled={disabled}
          onClick={() => onChange(null)}
        >
          <Cross2Icon />
        </ActionIcon>
      </Group>
      {row.progress !== undefined && (
        <Progress size="sm" value={row.progress * 100} />
      )}
    </Stack>
  )
}
